import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	forwardRef,
	HostListener,
	inject,
	Input,
	OnChanges,
	OnInit,
	signal,
	SimpleChanges,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";

import { IMyDate, IMyDayLabels } from "@intocare/ui-my-date-picker";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DateInputComponent),
	multi: true,
};

@Component({
	selector: "pv-date-input",
	styleUrls: ["input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DateInputComponent implements OnInit, ControlValueAccessor, OnChanges {
	private translator = inject(TranslateService);
	private cdRef = inject(ChangeDetectorRef);
	private elRef = inject(ElementRef);

	@Input() label: string;
	@Input() placeholder = "";
	@Input() inputClass = "";
	@Input() disabled = false;
	@Input() top = false;
	@Input() min: string = moment().subtract(100, "y").format("YYYY-MM-DD");
	@Input() max: string = moment().add(100, "y").format("YYYY-MM-DD");
	@Input() required = true;
	@Input() help: string;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() icon = "fa fa-calendar";
	/** Select the type of datepicker you want (etc. month for months only) */
	@Input() dateType: "day" | "month" = "day";

	value = signal<string>("");
	showDatePicker = false;
	locale = "";
	currentDate: moment.Moment;
	dayLabels: IMyDayLabels;
	selectedDate: IMyDate;
	datePickerOptions: any;

	private onTouch = () => {};
	private onModelChange = (_: unknown) => {};

	@HostListener("document:click", ["$event"])
	clickout(event) {
		if (!this.elRef.nativeElement.contains(event.target)) {
			this.showDatePicker = false;
		}
	}

	ngOnInit() {
		this.locale = this.translator.currentLang;
		moment.locale(this.locale);
		this.currentDate = moment();
		this.translator.get("labels.time").subscribe((time) => {
			this.dayLabels = {
				su: time.su,
				mo: time.mo,
				tu: time.tu,
				we: time.we,
				th: time.th,
				fr: time.fr,
				sa: time.sa,
			};
		});
		this.setDatePickerOptions();
	}

	writeValue(value: string) {
		this.value.set(value || "");
		if (value) {
			const momentDate = moment(value, "YYYY-MM-DD");
			this.value.set(momentDate.format(this.getPresentationFormat()));
			this.selectedDate = {
				day: momentDate.get("date"),
				month: (momentDate.get("month") as number) + 1,
				year: momentDate.get("year"),
			};
		}
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	onValueChange(value: string) {
		this.onTouch();
		const formattedDate = moment(this.padDateParts(value), this.getParserFormats(), this.translator.currentLang, true);
		if (!formattedDate.isValid()) {
			// This ensures that an incorrect date will result in an 'incorrect date' error
			this.onModelChange(value);
			this.value.set("");
			return;
		}
		this.onModelChange(formattedDate.format(this.getFormat()));
		this.value.set(formattedDate.format(this.getPresentationFormat()));
		this.selectedDate = {
			day: formattedDate.get("date"),
			month: (formattedDate.get("month") as number) + 1,
			year: formattedDate.get("year"),
		};
	}

	toggleDatePicker() {
		if (this.disabled) {
			return;
		}

		this.showDatePicker = !this.showDatePicker;
	}

	selectDate(event: any) {
		const value = moment(event.formatted, "YYYY-MM-DD");
		this.value.set(value.format(this.getPresentationFormat()));
		this.onModelChange(value.format(this.getFormat()));
		this.selectedDate = {
			day: value.get("date"),
			month: (value.get("month") as number) + 1,
			year: value.get("year"),
		};
		this.toggleDatePicker();
		this.onTouch();
	}

	private padDateParts(value: string) {
		return value
			.split("/")
			.map((part) => part.padStart(2, "0"))
			.join("/");
	}

	private getFormat() {
		return this.dateType === "month" ? "YYYY-MM" : "YYYY-MM-DD";
	}

	private getParserFormats() {
		return this.dateType === "month"
			? ["MMYYYY", "MMYY", "MM/YYYY", "MM/YY"]
			: ["DDMMYYYY", "DDMMYY", "DD/MM/YYYY", "DD/MM/YY"];
	}

	private getPresentationFormat() {
		return this.dateType === "month" ? "MM/YYYY" : "DD/MM/YYYY";
	}

	private setDatePickerOptions() {
		const dates = this.getMinMaxDates(this.min, this.max);
		this.translator.get(["labels.time"]).subscribe((translations) => {
			const todayBtn = translations.today;
			this.datePickerOptions = {
				inline: true,
				dateFormat: "yyyy-mm-dd",
				selectorHeight: "200px",
				selectorWidth: "180px",
				todayBtnTxt: todayBtn,
				hideMonthArrows: true,
				hideYearArrows: true,
				dayLabels: this.dayLabels,
				selectYearText: translations.selectYear,
				disableUntil: dates.min,
				disableSince: dates.max,
				selectMonthOnly: this.dateType === "month",
			};
		});
	}

	private getMinMaxDates(min: string, max: string) {
		const minDate = moment(min, "YYYY-MM-DD");
		const maxDate = moment(max, "YYYY-MM-DD");

		return {
			min: {
				day: minDate.format("D"),
				month: minDate.format("M"),
				year: minDate.format("YYYY"),
			},
			max: {
				day: maxDate.format("D"),
				month: maxDate.format("M"),
				year: maxDate.format("YYYY"),
			},
		};
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.min || changes.max || changes.type) {
			this.setDatePickerOptions();
		}
	}
}
